import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel,
  List, ListItemButton, ListItemText, MenuItem, Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import DraggableDialog from '../common/DraggableDialog';
import apiService from '../../services/api.service';
import { showSnackbar } from '../../store/slices/uiSlice';
import type { CompanyAccess, KindAccess } from '../../utils/userAccess';
import { useIsNarrow } from '../../hooks/useBreakpoints';

/**
 * User Management › Access: in each company of the user, the projects, divisions (SAP Dimension 1)
 * and departments (Dimension 2) they see beyond their own documents and approvals, each All or a
 * list of that company's codes (backend services/userAccess). A grant in one company never opens
 * the same code in another. Companies are given here too (All companies, Add a company); the
 * primary company and removing one stay in Manage Companies.
 */

type AccessUser = { id: number; fullname?: string; username?: string; role?: string };
type ListItem = { code: string; name: string; division?: string };
type ListKey = 'projects' | 'divisions' | 'departments';
/** Where a list came from: PRPO's synced copy of SAP, SAP itself (nothing synced), or neither. */
type ListSource = 'synced' | 'sap' | 'none';
type Lists = Record<ListKey, ListItem[]> & { sources?: Partial<Record<ListKey, ListSource>>; sapError?: string };
const LIST_KEYS: ListKey[] = ['projects', 'divisions', 'departments'];
/** "projects", "projects and divisions", "projects, divisions and departments". */
const joinWords = (words: string[]): string => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
type CompanyDetail = { companyId: number; companyName: string; databaseName: string; isPrimary: boolean; access: CompanyAccess; review: string[] };
type Detail = { allCompanies: boolean; companies: CompanyDetail[]; review: string[] };
type Kind = keyof CompanyAccess;
type CompanyOption = { companyId: number; companyName: string; databaseName: string };

const errorText = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback;

const KINDS: Array<{ key: Kind; title: string; allLabel: string; someLabel: string; allHelp: string; listKey: ListKey }> = [
  { key: 'projects', title: 'Projects', allLabel: 'All projects', someLabel: 'Only these projects', allHelp: 'Every project of this company, including projects added later.', listKey: 'projects' },
  { key: 'divisions', title: 'Divisions (SAP Dimension 1)', allLabel: 'All divisions', someLabel: 'Only these divisions', allHelp: 'Every division of this company: the Division of PR lines, subcontract lines and payment requests.', listKey: 'divisions' },
  { key: 'departments', title: 'Departments (SAP Dimension 2)', allLabel: 'All departments', someLabel: 'Only these departments', allHelp: 'Every department of this company: the cost center of Cost Center PRs and POs.', listKey: 'departments' },
];

const EMPTY: CompanyAccess = { projects: { all: false, codes: [] }, divisions: { all: false, codes: [] }, departments: { all: false, codes: [] } };

const tidy = (value: KindAccess): KindAccess => ({
  all: value.all === true,
  codes: value.all ? [] : Array.from(new Set((value.codes || []).map((c) => String(c).trim().toUpperCase()).filter(Boolean))).sort(),
});
const cloneAccess = (access?: CompanyAccess): CompanyAccess => ({
  projects: tidy(access?.projects || EMPTY.projects),
  divisions: tidy(access?.divisions || EMPTY.divisions),
  departments: tidy(access?.departments || EMPTY.departments),
});
/** One line per kind for the company list ("Divisions: AFM00"), or null when nothing is granted. */
const summaryLines = (access: CompanyAccess): string[] | null => {
  if (KINDS.every(({ key }) => !access[key].all && !access[key].codes.length)) return null;
  return KINDS.map(({ key, title }) => {
    const value = access[key];
    const label = title.split(' (')[0];
    const n = value.codes.length;
    return `${label}: ${value.all ? 'All' : !n ? 'none' : n > 3 ? `${n} ${label.toLowerCase()}` : value.codes.join(', ')}`;
  });
};

type Props = {
  open: boolean;
  user: AccessUser | null;
  onClose: () => void;
  /** Called after anything was saved, so the users list can refresh its access columns. */
  onChanged?: () => void;
  /** Opens Manage Companies for the user (primary company, removing a company). */
  onManageCompanies?: (user: AccessUser) => void;
};

export default function UserAccessDialog({ open, user, onClose, onChanged, onManageCompanies }: Props) {
  const dispatch = useDispatch();
  const narrow = useIsNarrow();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [lists, setLists] = useState<Record<number, Lists>>({});
  const [listsError, setListsError] = useState('');
  const [draft, setDraft] = useState<CompanyAccess>(cloneAccess());
  const [saving, setSaving] = useState(false);
  const [copyTarget, setCopyTarget] = useState<number | ''>('');
  const [activeCompanies, setActiveCompanies] = useState<CompanyOption[]>([]);
  const [addCompanyId, setAddCompanyId] = useState<number | ''>('');

  const notify = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') =>
    dispatch(showSnackbar({ message, severity })), [dispatch]);

  const load = useCallback(async (keep: number | null) => {
    if (!user) return;
    setLoading(true);
    try {
      const res: any = await apiService.getUserAccess(user.id);
      const next: Detail = res?.data || res;
      setDetail(next);
      const companies = next?.companies || [];
      const stay = keep !== null && companies.some((c) => c.companyId === keep) ? keep : null;
      setSelected(stay ?? (companies.find((c) => c.isPrimary) || companies[0])?.companyId ?? null);
    } catch (err: any) {
      notify(errorText(err, 'Failed to load the access of this user'), 'error');
    } finally {
      setLoading(false);
    }
  }, [user, notify]);

  useEffect(() => {
    if (!open) return;
    setDetail(null);
    setSelected(null);
    setLists({});
    setAddCompanyId('');
    load(null);
    apiService.getCompanies(true)
      .then((res: any) => {
        const raw = Array.isArray(res) ? res : res?.data || [];
        setActiveCompanies((raw as any[]).map((c: any) => ({
          companyId: Number(c.CompanyID ?? c.companyid),
          companyName: String(c.CompanyName ?? c.companyname ?? ''),
          databaseName: String(c.DatabaseName ?? c.databasename ?? ''),
        })).filter((c) => c.companyId > 0));
      })
      .catch(() => setActiveCompanies([]));
  }, [open, load]);

  const company = useMemo(() => detail?.companies.find((c) => c.companyId === selected) || null, [detail, selected]);

  useEffect(() => {
    setDraft(cloneAccess(company?.access));
    setCopyTarget('');
  }, [company]);

  const fetchLists = useCallback(async (companyId: number): Promise<Lists> => {
    const res: any = await apiService.getCompanyAccessLists(companyId);
    const data: Lists = res?.data || res || { projects: [], divisions: [], departments: [] };
    setLists((prev) => ({ ...prev, [companyId]: data }));
    return data;
  }, []);

  useEffect(() => {
    if (!selected || lists[selected]) return;
    setListsError('');
    fetchLists(selected).catch((err: any) => setListsError(err?.message || 'The lists of this company could not be loaded'));
  }, [selected, lists, fetchLists]);

  const current = selected ? lists[selected] : undefined;
  const fromSap = LIST_KEYS.filter((key) => current?.sources?.[key] === 'sap');
  const emptyText = (key: ListKey): string => {
    if (current?.sources?.[key] === 'sap') return `SAP has no ${key} in this company.`;
    if (current?.sapError) return `No ${key} are synced for this company, and SAP could not be read.`;
    return `This company has no synced ${key}.`;
  };
  const dirty = company ? JSON.stringify(cloneAccess(company.access)) !== JSON.stringify(cloneAccess(draft)) : false;
  const setKind = (key: Kind, value: KindAccess) => setDraft((prev) => ({ ...prev, [key]: tidy(value) }));
  const notes = [...(detail?.review || []), ...(company?.review || [])];

  const save = async () => {
    if (!user || !company) return;
    setSaving(true);
    try {
      await apiService.setUserCompanyAccess(user.id, company.companyId, cloneAccess(draft));
      notify(`Access saved for ${company.companyName}`);
      onChanged?.();
      await load(company.companyId);
    } catch (err: any) {
      notify(errorText(err, 'Failed to save the access'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const leaveUnsaved = () => !dirty || window.confirm('Leave this company without saving its changes?');

  const setAllCompanies = async (all: boolean) => {
    if (!user || !leaveUnsaved()) return;
    setSaving(true);
    try {
      await apiService.setUserAllCompanies(user.id, all);
      notify(all ? 'All companies given' : 'All companies removed: only the companies added to this user remain');
      onChanged?.();
      await load(selected);
    } catch (err: any) {
      notify(errorText(err, 'Failed to change All companies'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const addCompany = async (companyId: number) => {
    if (!user || !companyId || !leaveUnsaved()) return;
    setSaving(true);
    try {
      await apiService.addUserCompany(user.id, { companyid: companyId, isprimary: false });
      notify('Company added: now choose what the user sees in it');
      onChanged?.();
      await load(companyId);
    } catch (err: any) {
      notify(errorText(err, 'Failed to add the company'), 'error');
    } finally {
      setAddCompanyId('');
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!user || !company || !copyTarget) return;
    const target = detail?.companies.find((c) => c.companyId === Number(copyTarget));
    if (!target) return;
    if (!window.confirm(`Replace the projects, divisions and departments of ${target.companyName} with the ones of ${company.companyName}?`)) return;
    setSaving(true);
    try {
      const targetLists = lists[target.companyId] || await fetchLists(target.companyId);
      let skipped = 0;
      const pick = (key: Kind, items: ListItem[]): KindAccess => {
        const value = draft[key];
        if (value.all) return { all: true, codes: [] };
        const codes = value.codes.filter((code) => items.some((item) => item.code === code));
        skipped += value.codes.length - codes.length;
        return { all: false, codes };
      };
      const copied: CompanyAccess = {
        projects: pick('projects', targetLists.projects),
        divisions: pick('divisions', targetLists.divisions),
        departments: pick('departments', targetLists.departments),
      };
      await apiService.setUserCompanyAccess(user.id, target.companyId, copied);
      notify(`Copied to ${target.companyName}${skipped ? `: ${skipped} code(s) skipped, that company does not have them` : ''}`, skipped ? 'warning' : 'success');
      onChanged?.();
      await load(company.companyId);
    } catch (err: any) {
      notify(errorText(err, 'Failed to copy the access'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async () => {
    if (!user) return;
    try {
      await apiService.clearUserAccessReview(user.id);
      notify('Marked as reviewed');
      onChanged?.();
      await load(selected);
    } catch (err: any) {
      notify(errorText(err, 'Failed to clear the review notes'), 'error');
    }
  };

  const title = `Access for ${user?.fullname || user?.username || ''}`;
  const actions = (
    <>
      <Button onClick={onClose}>Close</Button>
      <Button variant="contained" onClick={save} disabled={!company || !dirty || saving}>
        {saving ? 'Saving…' : 'Save access'}
      </Button>
    </>
  );

  return (
    <DraggableDialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={narrow} title={title} actions={actions}>
      {loading && !detail ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress aria-label="Loading access" /></Box>
      ) : (
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {user?.role ? `${user.role}: ` : ''}what this user sees beyond their own documents and approvals, set for each
            company separately. A project or department granted in one company never opens the same code in another one.
          </Typography>
          {user?.role === 'Admin' && (
            <Alert severity="info">
              An Admin sees every company, project, division and department, whatever is set here, except in Project
              Reports and MCSR: there the projects granted in a company limit an Admin too.
            </Alert>
          )}
          {detail && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControlLabel
                  sx={{ flex: '1 1 auto', mr: 0 }}
                  control={<Switch checked={detail.allCompanies} disabled={saving} onChange={(e) => setAllCompanies(e.target.checked)} />}
                  label={
                    <span>
                      <strong>All companies</strong>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        {detail.allCompanies
                          ? 'Every active company, including companies added later. A new company starts with nothing granted.'
                          : 'Off: only the companies added to this user.'}
                      </Typography>
                    </span>
                  }
                />
                {!detail.allCompanies && (
                  <FormControl size="small" sx={{ minWidth: { sm: 260 } }}>
                    <InputLabel id="access-add-company">Add a company</InputLabel>
                    <Select
                      labelId="access-add-company"
                      label="Add a company"
                      value={addCompanyId}
                      disabled={saving}
                      onChange={(e) => { const id = Number(e.target.value) || 0; setAddCompanyId(id || ''); if (id) addCompany(id); }}
                    >
                      {activeCompanies.filter((c) => !detail.companies.some((x) => x.companyId === c.companyId)).map((c) => (
                        <MenuItem key={c.companyId} value={c.companyId}>{c.companyName}{c.databaseName ? ` (${c.databaseName})` : ''}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
              {onManageCompanies && user && detail.companies.length > 0 && (
                <Button size="small" sx={{ mt: 1 }} disabled={saving} onClick={() => { if (leaveUnsaved()) onManageCompanies(user); }}>
                  Primary company / remove a company
                </Button>
              )}
            </Box>
          )}
          {notes.length > 0 && (
            <Alert
              severity="warning"
              action={<Button color="inherit" size="small" onClick={markReviewed} disabled={saving}>Mark reviewed</Button>}
            >
              <Typography variant="subtitle2">To review (the move to per-company access)</Typography>
              {notes.map((note, i) => <Typography key={i} variant="body2">• {note}</Typography>)}
            </Alert>
          )}
          {detail && detail.companies.length === 0 ? (
            <Alert severity="info">
              This user has no company yet: add one above, or switch on All companies. Then choose what they see in it.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 260px', maxWidth: { md: 340 }, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">Access per company</Typography>
                <List dense disablePadding aria-label="Companies of this user">
                  {(detail?.companies || []).map((c) => (
                    <ListItemButton
                      key={c.companyId}
                      selected={c.companyId === selected}
                      onClick={() => { if (c.companyId !== selected && leaveUnsaved()) setSelected(c.companyId); }}
                      sx={{ border: 1, borderColor: c.companyId === selected ? 'primary.main' : 'divider', borderRadius: 1, mb: 1, alignItems: 'flex-start' }}
                    >
                      <ListItemText
                        primary={`${c.companyName}${c.isPrimary ? ' (primary)' : ''}`}
                        secondary={<>
                          <span style={{ display: 'block' }}>{c.databaseName}</span>
                          {(summaryLines(c.access) || ['Nothing granted: own documents and approvals only']).map((text) => (
                            <span key={text} style={{ display: 'block' }}>{text}</span>
                          ))}
                          {c.review.length > 0 && <span style={{ display: 'block', color: '#8a4b00' }}>To review</span>}
                        </>}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>

              {company && (
                <Box sx={{ flex: '999 1 520px', minWidth: 0 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="h6" sx={{ fontSize: 17 }}>{company.companyName}</Typography>
                      <Chip size="small" label={company.databaseName} />
                      {!current && !listsError && <CircularProgress size={16} aria-label="Loading the company lists" />}
                      {current && (
                        <Typography variant="caption" color="text.secondary">
                          {current.projects.length} projects · {current.divisions.length} divisions · {current.departments.length} departments
                        </Typography>
                      )}
                    </Stack>
                    {listsError && <Alert severity="error">{listsError}</Alert>}
                    {current && fromSap.length > 0 && (
                      <Alert severity="info">
                        PRPO has no synced copy of this company&apos;s {joinWords(fromSap)}, so {fromSap.length > 1 ? 'these lists are' : 'this list is'} read
                        from SAP.
                      </Alert>
                    )}
                    {current?.sapError && <Alert severity="warning">SAP could not be read for this company&apos;s lists: {current.sapError}</Alert>}

                    {KINDS.map(({ key, title: kindTitle, allLabel, someLabel, allHelp, listKey }) => {
                      const value = draft[key];
                      const items = current?.[listKey] || [];
                      const unknown = value.codes.filter((code) => !items.some((item) => item.code === code));
                      return (
                        <Box key={key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                            <Typography variant="subtitle2">{kindTitle}</Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={value.all ? 'all' : 'some'}
                              onChange={(_e, next) => { if (next) setKind(key, { all: next === 'all', codes: value.codes }); }}
                              aria-label={kindTitle}
                            >
                              <ToggleButton value="all">{allLabel}</ToggleButton>
                              <ToggleButton value="some">{someLabel}</ToggleButton>
                            </ToggleButtonGroup>
                          </Stack>
                          {value.all ? (
                            <Typography variant="body2" color="text.secondary">{allHelp}</Typography>
                          ) : key === 'projects' ? (
                            <Stack spacing={1}>
                              <Autocomplete
                                multiple
                                filterSelectedOptions
                                limitTags={12}
                                options={items}
                                value={value.codes.map((code) => items.find((item) => item.code === code) || { code, name: '' })}
                                isOptionEqualToValue={(a, b) => a.code === b.code}
                                getOptionLabel={(o) => `${o.code}${o.name ? ` · ${o.name}` : ''}`}
                                onChange={(_e, next) => setKind(key, { all: false, codes: next.map((o) => o.code) })}
                                renderInput={(params) => <TextField {...params} label={`Projects of this company (${items.length})`} placeholder="Search by code or name" />}
                              />
                              <FormControl size="small" sx={{ maxWidth: 360 }}>
                                <InputLabel id={`by-division-${company.companyId}`}>Add every project of a division</InputLabel>
                                <Select
                                  labelId={`by-division-${company.companyId}`}
                                  label="Add every project of a division"
                                  value=""
                                  onChange={(e) => {
                                    const division = String(e.target.value || '');
                                    if (!division) return;
                                    setKind(key, { all: false, codes: [...value.codes, ...items.filter((p) => p.division === division).map((p) => p.code)] });
                                  }}
                                >
                                  {(current?.divisions || []).map((d) => (
                                    <MenuItem key={d.code} value={d.code}>
                                      {d.code} · {d.name} ({items.filter((p) => p.division === d.code).length} projects)
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {current && items.length === 0 && <Typography variant="body2" color="text.secondary">{emptyText(listKey)}</Typography>}
                              {value.codes.length === 0 && <Typography variant="body2" color="warning.main">No project granted in this company.</Typography>}
                            </Stack>
                          ) : (
                            <Stack spacing={1}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', columnGap: 1 }}>
                                {items.map((item) => (
                                  <FormControlLabel
                                    key={item.code}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={value.codes.includes(item.code)}
                                        onChange={(e) => setKind(key, {
                                          all: false,
                                          codes: e.target.checked ? [...value.codes, item.code] : value.codes.filter((c) => c !== item.code),
                                        })}
                                      />
                                    }
                                    label={<span><strong>{item.code}</strong> {item.name}</span>}
                                  />
                                ))}
                              </Box>
                              {unknown.length > 0 && (
                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">Not in this company&apos;s list:</Typography>
                                  {unknown.map((code) => (
                                    <Chip key={code} size="small" label={code} onDelete={() => setKind(key, { all: false, codes: value.codes.filter((c) => c !== code) })} />
                                  ))}
                                </Stack>
                              )}
                              {current && items.length === 0 && <Typography variant="body2" color="text.secondary">{emptyText(listKey)}</Typography>}
                              {value.codes.length === 0 && <Typography variant="body2" color="warning.main">None granted in this company.</Typography>}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}

                    {(detail?.companies.length || 0) > 1 && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                        <FormControl size="small" sx={{ minWidth: { sm: 260 }, flex: '1 1 260px' }}>
                          <InputLabel id="copy-access-to">Copy these settings to</InputLabel>
                          <Select labelId="copy-access-to" label="Copy these settings to" value={copyTarget} onChange={(e) => setCopyTarget(e.target.value === '' ? '' : Number(e.target.value))}>
                            {(detail?.companies || []).filter((c) => c.companyId !== company.companyId).map((c) => (
                              <MenuItem key={c.companyId} value={c.companyId}>{c.companyName} ({c.databaseName})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button onClick={copy} disabled={!copyTarget || saving || dirty}>Copy</Button>
                        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                          {dirty
                            ? 'Save this company first; then its settings can be copied.'
                            : 'Saves them in that company at once, replacing what it had; codes it does not have are skipped.'}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      )}
    </DraggableDialog>
  );
}
